import * as React from "react";
import { cn } from "../../lib/utils";

export interface AutoExpandingTextareaProps
  extends Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    "onKeyDown" | "onPaste" | "onCut"
  > {
  minRows?: number;
  maxRows?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onCut?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
}

const AutoExpandingTextarea = React.forwardRef<
  HTMLTextAreaElement,
  AutoExpandingTextareaProps
>(
  (
    {
      className,
      minRows = 2,
      maxRows = 8,
      value,
      onChange,
      onKeyDown,
      onPaste,
      onCut,
      ...props
    },
    ref
  ) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const [currentRows, setCurrentRows] = React.useState(minRows);

    // Combine refs
    React.useImperativeHandle(ref, () => textareaRef.current!);

    const calculateRows = React.useCallback(
      (content: string) => {
        if (!textareaRef.current) return minRows;

        const textarea = textareaRef.current;

        // If content is empty or just whitespace, return minimum rows
        if (!content || content.trim() === "") {
          return minRows;
        }

        // Count actual line breaks in content
        const lineBreaks = (content.match(/\n/g) || []).length;
        const contentLines = lineBreaks + 1;

        // For a more accurate calculation, we'll use a temporary measurement
        const computedStyle = window.getComputedStyle(textarea);

        // Get the line height - use a more reliable calculation
        let lineHeight = parseInt(computedStyle.lineHeight);
        if (isNaN(lineHeight) || lineHeight === 0) {
          const fontSize = parseInt(computedStyle.fontSize) || 14;
          lineHeight = Math.floor(fontSize * 1.5);
        }

        // Store original styles
        const originalHeight = textarea.style.height;
        const originalOverflow = textarea.style.overflow;
        const originalResize = textarea.style.resize;
        const originalRows = textarea.rows;

        // Reset to minimal state for accurate measurement
        textarea.style.height = "auto";
        textarea.style.overflow = "hidden";
        textarea.style.resize = "none";
        textarea.rows = 1;

        // Force a reflow to get accurate scrollHeight
        textarea.scrollTop = 0;
        const scrollHeight = textarea.scrollHeight;

        const paddingTop = parseInt(computedStyle.paddingTop) || 0;
        const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
        const borderTop = parseInt(computedStyle.borderTopWidth) || 0;
        const borderBottom = parseInt(computedStyle.borderBottomWidth) || 0;

        // Calculate content height
        const contentHeight =
          scrollHeight - paddingTop - paddingBottom - borderTop - borderBottom;

        // Calculate rows needed based on content height
        const calculatedRows = Math.max(
          1,
          Math.ceil(contentHeight / lineHeight)
        );

        // Use the maximum of line-break-based calculation and height-based calculation
        const neededRows = Math.max(contentLines, calculatedRows);

        // Restore original styles
        textarea.style.height = originalHeight;
        textarea.style.overflow = originalOverflow;
        textarea.style.resize = originalResize;
        textarea.rows = originalRows;

        return Math.max(minRows, Math.min(maxRows, neededRows));
      },
      [minRows, maxRows]
    );

    const updateHeight = React.useCallback(() => {
      if (!textareaRef.current) return;

      const content = textareaRef.current.value;
      const newRows = calculateRows(content);

      // Always update rows if they're different - this ensures shrinking works
      if (newRows !== currentRows) {
        setCurrentRows(newRows);
      }

      // Set overflow based on whether we've hit max rows
      const isAtMaxRows = newRows >= maxRows;
      textareaRef.current.style.overflowY = isAtMaxRows ? "auto" : "hidden";

      // Reset scroll position when not at max rows (content shrunk)
      if (!isAtMaxRows && textareaRef.current.scrollTop > 0) {
        textareaRef.current.scrollTop = 0;
      }

      // Maintain cursor position when scrolling is enabled
      if (isAtMaxRows) {
        const cursorPosition = textareaRef.current.selectionStart;
        const textBeforeCursor = content.substring(0, cursorPosition);
        const lines = textBeforeCursor.split("\n");
        const currentLine = lines.length;

        // If cursor is near the end, scroll to bottom
        if (currentLine > maxRows - 1) {
          requestAnimationFrame(() => {
            if (textareaRef.current) {
              textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
            }
          });
        }
      }
    }, [calculateRows, currentRows, maxRows]);

    // Update height when value changes
    React.useEffect(() => {
      updateHeight();
    }, [value, updateHeight]);

    // Update height on mount
    React.useEffect(() => {
      updateHeight();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e);
      // Height will be updated by the useEffect watching the value
    };

    const handleInput = () => {
      // Update height immediately on input for smoother experience
      updateHeight();
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Call original onPaste if provided
      onPaste?.(e);

      // Update height after paste operation
      setTimeout(updateHeight, 0);
    };

    const handleCut = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Call original onCut if provided
      onCut?.(e);

      // Update height after cut operation
      setTimeout(updateHeight, 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Call the original onKeyDown if provided
      onKeyDown?.(e);

      // Handle specific key behaviors for better UX
      if (e.key === "Tab" && !e.shiftKey) {
        // Allow tab to work normally (don't prevent default)
        return;
      }

      if (e.key === "Enter" && e.shiftKey) {
        // Shift+Enter should always create a new line
        // This is handled by default, just ensure height updates
        setTimeout(updateHeight, 0);
      }

      // Handle deletion keys that might reduce content
      if (e.key === "Backspace" || e.key === "Delete") {
        // Update height after deletion
        setTimeout(updateHeight, 0);
      }
    };

    // Calculate the height based on rows
    const lineHeight = 1.5; // rem units
    const padding = 0.5; // rem units (py-2 = 0.5rem top + 0.5rem bottom)
    const border = 0.125; // rem units (border width)
    const height = `${currentRows * lineHeight + padding + border}rem`;

    return (
      <textarea
        ref={textareaRef}
        className={cn(
          "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none transition-all duration-200 ease-out",
          className
        )}
        style={{
          height,
          minHeight: `${minRows * lineHeight + padding + border}rem`,
          maxHeight: `${maxRows * lineHeight + padding + border}rem`,
        }}
        value={value}
        onChange={handleChange}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onCut={handleCut}
        rows={currentRows}
        aria-label={
          props["aria-label"] ||
          props.placeholder ||
          "Auto-expanding text input"
        }
        {...props}
      />
    );
  }
);

AutoExpandingTextarea.displayName = "AutoExpandingTextarea";

export { AutoExpandingTextarea };
